import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useManualFetch, type ManualFetchResult } from '@/lib/admin/hooks/useManualFetch';
import { toNumber } from '@/lib/admin/utils/format-bytes';

// ─── Response types (contratto RPC lato Supabase) ──────────────────────────────

/** Riga singola di `admin_database_overview()`. */
export interface DatabaseOverview {
  database_bytes: number;
  total_table_bytes: number;
  total_index_bytes: number;
  total_toast_bytes: number;
  table_count: number;
}

/** Riga di `admin_table_sizes()`. */
export interface TableSizeRow {
  schema_name: string;
  table_name: string;
  total_bytes: number;
  table_bytes: number;
  index_bytes: number;
  toast_bytes: number;
  row_estimate: number;
}

/** Riga di `admin_index_sizes()`. */
export interface IndexSizeRow {
  schema_name: string;
  table_name: string;
  index_name: string;
  size_bytes: number;
  index_scans: number;
}

/** Riga di `admin_table_bloat()`. */
export interface TableBloatRow {
  schema_name: string;
  table_name: string;
  live_rows: number;
  dead_rows: number;
  dead_pct: number;
  last_vacuum: string | null;
  last_autovacuum: string | null;
}

/** Riga di `admin_storage_stats()`. */
export interface StorageBucketRow {
  bucket_id: string;
  object_count: number;
  total_bytes: number;
}

export interface DatabaseSizeStats {
  overview: DatabaseOverview | null;
  tables: TableSizeRow[];
  indexes: IndexSizeRow[];
  bloat: TableBloatRow[];
  storage: StorageBucketRow[];
}

// ─── RPC access ────────────────────────────────────────────────────────────────

/**
 * Le RPC di diagnostica non sono ancora in `types.ts` (generato dallo schema):
 * un unico cast localizzato verso una firma minimale, invece di spargere `any`
 * su ogni chiamata.
 */
type RawRow = Record<string, unknown>;
type UntypedRpc = (
  fn: string
) => Promise<{ data: RawRow | RawRow[] | null; error: { message: string } | null }>;

const rpc = supabase.rpc.bind(supabase) as unknown as UntypedRpc;

async function callRpc(fn: string): Promise<RawRow[]> {
  const { data, error } = await rpc(fn);
  if (error) throw new Error(`${fn}: ${error.message}`);
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
}

// ─── Mapping difensivo (bigint → number) ───────────────────────────────────────

function mapOverview(row: RawRow | undefined): DatabaseOverview | null {
  if (!row) return null;
  return {
    database_bytes: toNumber(row.database_bytes as number | string | null),
    total_table_bytes: toNumber(row.total_table_bytes as number | string | null),
    total_index_bytes: toNumber(row.total_index_bytes as number | string | null),
    total_toast_bytes: toNumber(row.total_toast_bytes as number | string | null),
    table_count: toNumber(row.table_count as number | string | null),
  };
}

function mapTable(row: RawRow): TableSizeRow {
  return {
    schema_name: String(row.schema_name ?? ''),
    table_name: String(row.table_name ?? ''),
    total_bytes: toNumber(row.total_bytes as number | string | null),
    table_bytes: toNumber(row.table_bytes as number | string | null),
    index_bytes: toNumber(row.index_bytes as number | string | null),
    toast_bytes: toNumber(row.toast_bytes as number | string | null),
    row_estimate: toNumber(row.row_estimate as number | string | null),
  };
}

function mapIndex(row: RawRow): IndexSizeRow {
  return {
    schema_name: String(row.schema_name ?? ''),
    table_name: String(row.table_name ?? ''),
    index_name: String(row.index_name ?? ''),
    size_bytes: toNumber(row.size_bytes as number | string | null),
    index_scans: toNumber(row.index_scans as number | string | null),
  };
}

function mapBloat(row: RawRow): TableBloatRow {
  return {
    schema_name: String(row.schema_name ?? ''),
    table_name: String(row.table_name ?? ''),
    live_rows: toNumber(row.live_rows as number | string | null),
    dead_rows: toNumber(row.dead_rows as number | string | null),
    dead_pct: toNumber(row.dead_pct as number | string | null),
    last_vacuum: (row.last_vacuum as string | null) ?? null,
    last_autovacuum: (row.last_autovacuum as string | null) ?? null,
  };
}

function mapStorage(row: RawRow): StorageBucketRow {
  return {
    bucket_id: String(row.bucket_id ?? ''),
    object_count: toNumber(row.object_count as number | string | null),
    total_bytes: toNumber(row.total_bytes as number | string | null),
  };
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export type UseDatabaseSizeStatsResult = ManualFetchResult<DatabaseSizeStats>;

/**
 * Statistiche di peso di database e storage.
 *
 * Sono query pesanti sui cataloghi Postgres: il fetch parte SOLO quando si
 * chiama `load()`. I dati restano in memoria finché non si richiama `load()`.
 */
export function useDatabaseSizeStats(): UseDatabaseSizeStatsResult {
  const fetcher = useCallback(async (): Promise<DatabaseSizeStats> => {
    const [overviewRows, tableRows, indexRows, bloatRows, storageRows] = await Promise.all([
      callRpc('admin_database_overview'),
      callRpc('admin_table_sizes'),
      callRpc('admin_index_sizes'),
      callRpc('admin_table_bloat'),
      callRpc('admin_storage_stats'),
    ]);

    return {
      overview: mapOverview(overviewRows[0]),
      tables: tableRows.map(mapTable),
      indexes: indexRows.map(mapIndex),
      bloat: bloatRows.map(mapBloat),
      storage: storageRows.map(mapStorage),
    };
  }, []);

  return useManualFetch(fetcher, 'useDatabaseSizeStats');
}

export default useDatabaseSizeStats;
